import { industries as industryBase, aiSolutions } from "@/lib/data/site";

export type LandingFaq = { question: string; answer: string };

export type IndustryLanding = {
  name: string;
  slug: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  challenges: string[];
  capabilities: string[];
  relatedServices: string[]; // service hrefs
  faqs: LandingFaq[];
};

export type SolutionLanding = {
  title: string;
  slug: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  outcomes: string[];
  relatedServices: string[];
  faqs: LandingFaq[];
};

const industryExtras: Record<
  string,
  Omit<IndustryLanding, "name" | "slug" | "description">
> = {
  "financial-services": {
    seoTitle: "Fintech & Financial Services Software Development",
    seoDescription:
      "Custom fintech software, banking platforms, risk systems, and AI for financial services — secure, compliant engineering from Maxridge in Noida for global clients.",
    keywords: [
      "fintech software development company",
      "banking software development",
      "financial services software development",
      "wealth management software",
      "regulatory compliance software",
    ],
    challenges: [
      "Legacy cores and fragmented customer data slow digital products.",
      "Compliance and audit requirements block rapid AI adoption.",
      "Support teams struggle to find policies and product knowledge quickly.",
    ],
    capabilities: [
      "Customer and advisor copilots with governed RAG",
      "Risk, KYC, and operations automation with audit trails",
      "Modern web/mobile experiences on secure cloud platforms",
      "Data platforms for analytics, reporting, and model features",
    ],
    relatedServices: [
      "/services/artificial-intelligence",
      "/services/enterprise-software",
      "/services/data-engineering",
      "/services/custom-software-development",
    ],
    faqs: [
      {
        question: "Do you build for regulated financial environments?",
        answer:
          "Yes. We design with access control, audit logging, data residency, and review workflows so AI and digital products fit regulated operating models.",
      },
      {
        question: "Can you modernize without a full core replacement?",
        answer:
          "Often yes. We wrap and integrate legacy cores with APIs, event streams, and new product surfaces so you can ship value while planning deeper modernization.",
      },
    ],
  },
  healthcare: {
    seoTitle: "Healthcare Software & AI Development Company",
    seoDescription:
      "Healthcare software development, clinical operations platforms, and responsible AI — HIPAA-minded engineering patterns for providers, payers, and healthtech products.",
    keywords: [
      "healthcare software development company",
      "healthtech software development",
      "clinical AI development",
      "telemedicine app development",
      "healthcare data platform",
    ],
    challenges: [
      "Clinical and operational data is siloed across EMR and ancillary systems.",
      "Staff waste time on repetitive documentation and coordination tasks.",
      "AI pilots stall without privacy, evaluation, and workflow fit.",
    ],
    capabilities: [
      "Care operations and patient engagement applications",
      "Secure data pipelines for analytics and decision support",
      "Assistive AI for documentation and knowledge retrieval",
      "Interoperable APIs and integration with existing clinical systems",
    ],
    relatedServices: [
      "/services/artificial-intelligence",
      "/services/data-engineering",
      "/services/web-app-development",
      "/services/system-integration",
    ],
    faqs: [
      {
        question: "How do you approach healthcare data sensitivity?",
        answer:
          "We minimize data exposure, separate environments, apply role-based access, and design retrieval/AI features with clear audit and human-review paths.",
      },
    ],
  },
  retail: {
    seoTitle: "Retail & E-commerce Software Development",
    seoDescription:
      "E-commerce platforms, personalization, inventory intelligence, and omnichannel retail software — custom product engineering for growth-stage and enterprise retail brands.",
    keywords: [
      "ecommerce software development company",
      "retail software development",
      "omnichannel commerce development",
      "personalization engine development",
      "inventory management software",
    ],
    challenges: [
      "Customer journeys break across web, mobile, and store systems.",
      "Inventory and pricing signals arrive too late for operators.",
      "Personalization experiments never reach reliable production.",
    ],
    capabilities: [
      "Composable commerce and custom storefronts",
      "Recommendation and personalization services",
      "Inventory and fulfillment intelligence",
      "Mobile shopping and associate apps",
    ],
    relatedServices: [
      "/services/web-app-development",
      "/services/mobile-app-development",
      "/services/artificial-intelligence",
      "/services/data-engineering",
    ],
    faqs: [
      {
        question: "Can you integrate with Shopify, Magento, or custom stacks?",
        answer:
          "Yes. We extend existing commerce platforms or build custom storefronts and services when you need deeper differentiation.",
      },
    ],
  },
  manufacturing: {
    seoTitle: "Manufacturing Software & Industrial AI Development",
    seoDescription:
      "Manufacturing software, plant data platforms, predictive maintenance, and industrial AI — connect OT/IT data and ship decision systems for production operations.",
    keywords: [
      "manufacturing software development",
      "industrial IoT software development",
      "predictive maintenance software",
      "digital twin development",
      "smart factory software",
    ],
    challenges: [
      "Plant and enterprise data stay disconnected from decision tools.",
      "Downtime and quality issues are detected too late.",
      "Dashboards exist, but operators lack actionable recommendations.",
    ],
    capabilities: [
      "OT/IT data pipelines and lakehouse foundations",
      "Predictive maintenance and quality models",
      "Operator dashboards and alerting workflows",
      "Secure cloud and edge deployment patterns",
    ],
    relatedServices: [
      "/services/data-engineering",
      "/services/artificial-intelligence",
      "/services/cloud-devops",
      "/services/enterprise-software",
    ],
    faqs: [
      {
        question: "Do you work with both brownfield plants and greenfield builds?",
        answer:
          "Yes. We start from available sensors, historians, and ERP data, then expand coverage as instrumentation and process maturity grow.",
      },
    ],
  },
  technology: {
    seoTitle: "SaaS Product Engineering & Technology Software Development",
    seoDescription:
      "SaaS product engineering, platform modernization, and AI-native software for technology companies — MVP to scale with dedicated product teams from Maxridge.",
    keywords: [
      "SaaS product development company",
      "software product engineering",
      "AI-native SaaS development",
      "platform modernization services",
      "startup software development company",
    ],
    challenges: [
      "Roadmaps stall between MVP speed and enterprise-ready architecture.",
      "AI features are bolted on without evaluation or reliability.",
      "Hiring full-time specialists for every stack is slow and expensive.",
    ],
    capabilities: [
      "End-to-end product engineering and dedicated teams",
      "AI features grounded in your product data and workflows",
      "Cloud-native platforms with CI/CD and observability",
      "MVP acceleration with a path to production scale",
    ],
    relatedServices: [
      "/services/mvp-development",
      "/services/hire-dedicated-developers",
      "/services/artificial-intelligence",
      "/services/custom-software-development",
    ],
    faqs: [
      {
        question: "Can Maxridge act as an extension of our product team?",
        answer:
          "Yes. We commonly embed as a dedicated pod alongside your PMs and founders, owning features end-to-end with clear rituals and delivery metrics.",
      },
    ],
  },
  logistics: {
    seoTitle: "Logistics & Supply Chain Software Development",
    seoDescription:
      "Logistics software, supply-chain visibility, routing, and operations platforms — real-time systems for freight, 3PL, and distribution businesses.",
    keywords: [
      "logistics software development company",
      "supply chain software development",
      "fleet management software development",
      "warehouse management system development",
      "transportation management software",
    ],
    challenges: [
      "Shipments, inventory, and partners live in disconnected tools.",
      "Exceptions are handled manually and late.",
      "Customers lack reliable tracking and ETA accuracy.",
    ],
    capabilities: [
      "Visibility platforms and partner portals",
      "Event-driven tracking and exception workflows",
      "Optimization and forecasting with operational data",
      "Mobile apps for drivers, warehouse, and field teams",
    ],
    relatedServices: [
      "/services/custom-software-development",
      "/services/data-engineering",
      "/services/mobile-app-development",
      "/services/system-integration",
    ],
    faqs: [
      {
        question: "Can you integrate carrier and ERP systems?",
        answer:
          "Yes. We build and maintain integrations across ERPs, WMS/TMS, carrier APIs, and customer channels so operations run on one reliable data flow.",
      },
    ],
  },
  education: {
    seoTitle: "EdTech & Education Software Development",
    seoDescription:
      "EdTech product development, learning platforms, and institutional software — adaptive learning, admin systems, and AI-assisted education experiences.",
    keywords: [
      "edtech software development company",
      "learning management system development",
      "education app development",
      "adaptive learning platform development",
      "online education software",
    ],
    challenges: [
      "Learners and faculty juggle fragmented content and admin tools.",
      "Personalization is shallow or impossible at scale.",
      "Institutions need modernization without disrupting operations.",
    ],
    capabilities: [
      "Learning platforms and content delivery systems",
      "Student and faculty portals",
      "AI tutors and knowledge assistants with oversight",
      "Analytics for engagement and outcomes",
    ],
    relatedServices: [
      "/services/web-app-development",
      "/services/mobile-app-development",
      "/services/artificial-intelligence",
      "/services/mvp-development",
    ],
    faqs: [
      {
        question: "Do you build consumer EdTech and institutional systems?",
        answer:
          "Both. We ship learner-facing products and the admin, content, and analytics layers institutions need to operate reliably.",
      },
    ],
  },
  "public-sector": {
    seoTitle: "Public Sector Digital Transformation & Software Development",
    seoDescription:
      "Government and public-sector software development — citizen services, secure platforms, and digital transformation with accessibility and auditability in mind.",
    keywords: [
      "government software development company",
      "public sector digital transformation",
      "citizen services software development",
      "e-governance software development",
      "secure government application development",
    ],
    challenges: [
      "Citizen journeys span paper, portals, and disconnected departments.",
      "Security and procurement constraints slow delivery.",
      "Legacy systems block modern digital experiences.",
    ],
    capabilities: [
      "Citizen-facing portals and service workflows",
      "Secure integrations across departmental systems",
      "Accessibility-minded UI and multilingual support",
      "Cloud platforms with strong identity and audit controls",
    ],
    relatedServices: [
      "/services/enterprise-software",
      "/services/system-integration",
      "/services/cloud-devops",
      "/services/it-consulting",
    ],
    faqs: [
      {
        question: "Can you work within public procurement and security reviews?",
        answer:
          "Yes. We document architecture, access models, and delivery plans in ways that support security reviews and multi-stakeholder governance.",
      },
    ],
  },
  telecommunications: {
    seoTitle: "Telecom Software Development & Network Intelligence",
    seoDescription:
      "Telecommunications software development — customer systems, network intelligence, automation, and digital platforms for telecom operators and vendors.",
    keywords: [
      "telecom software development company",
      "BSS OSS software development",
      "telecom customer experience software",
      "network automation software",
      "telecom AI solutions",
    ],
    challenges: [
      "Customer and network systems evolve faster than integration layers.",
      "High call volumes and repetitive support inflate cost-to-serve.",
      "Automation is fragmented across tools and vendors.",
    ],
    capabilities: [
      "Customer care and self-service platforms",
      "Knowledge copilots for agents and field teams",
      "Data and AI for network and customer signals",
      "API-led integration across BSS/OSS landscapes",
    ],
    relatedServices: [
      "/services/artificial-intelligence",
      "/services/system-integration",
      "/services/data-engineering",
      "/services/enterprise-software",
    ],
    faqs: [
      {
        question: "Do you modernize BSS/OSS-adjacent experiences?",
        answer:
          "We focus on customer, partner, and operations experiences that integrate with BSS/OSS systems — accelerating outcomes without requiring a full stack rewrite.",
      },
    ],
  },
};

function slugifySolution(title: string) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const solutionExtras: Record<string, Omit<SolutionLanding, "title" | "slug" | "description">> = {
  "ai-copilots": {
    seoTitle: "Enterprise AI Copilot Development Company",
    seoDescription:
      "Build production AI copilots for employees and customers — grounded knowledge, tool access, and governed answers with Maxridge AI engineering teams.",
    keywords: [
      "AI copilot development",
      "enterprise AI assistant development",
      "knowledge copilot company",
      "workplace AI copilot",
      "AI assistant development company",
    ],
    outcomes: [
      "Faster knowledge access across policies and systems",
      "Reduced repetitive support load",
      "Audit-friendly retrieval and response patterns",
    ],
    relatedServices: ["/services/artificial-intelligence", "/services/data-engineering", "/services/enterprise-software"],
    faqs: [
      {
        question: "What makes an enterprise copilot different from a chatbot?",
        answer:
          "Enterprise copilots connect to your systems of record, respect permissions, ground answers in approved knowledge, and include evaluation plus human escalation paths.",
      },
    ],
  },
  "ai-agents": {
    seoTitle: "AI Agent Development Company",
    seoDescription:
      "Design and ship AI agents that execute multi-step business workflows with tools, guardrails, and observability — production agentic systems from Maxridge.",
    keywords: [
      "AI agent development company",
      "agentic AI development",
      "autonomous AI agent development",
      "multi-agent system development",
      "AI workflow automation",
    ],
    outcomes: [
      "Automated multi-step processes across tools",
      "Lower manual coordination cost",
      "Observable agent runs with clear failure handling",
    ],
    relatedServices: ["/services/artificial-intelligence", "/services/system-integration", "/services/cloud-devops"],
    faqs: [
      {
        question: "Can agents safely take actions in business systems?",
        answer:
          "Yes, when scoped carefully. We define allowed tools, approval gates for high-risk actions, logging, and evaluation before expanding autonomy.",
      },
    ],
  },
  "enterprise-rag": {
    seoTitle: "Enterprise RAG Development Company",
    seoDescription:
      "Enterprise RAG platforms that turn documents and systems into grounded, auditable answers — vector search, permissions, evaluation, and production ops.",
    keywords: [
      "enterprise RAG development",
      "RAG development company",
      "retrieval augmented generation services",
      "enterprise knowledge base AI",
      "vector search development",
    ],
    outcomes: [
      "Trusted answers grounded in your knowledge",
      "Permission-aware retrieval",
      "Evaluation loops that improve answer quality over time",
    ],
    relatedServices: ["/services/artificial-intelligence", "/services/data-engineering", "/services/custom-software-development"],
    faqs: [
      {
        question: "Do you support hybrid search and citations?",
        answer:
          "Yes. We combine keyword and vector retrieval, chunking strategies, and citation UX so users can verify sources quickly.",
      },
    ],
  },
  "intelligent-automation": {
    seoTitle: "Intelligent Process Automation & AI Workflow Services",
    seoDescription:
      "Intelligent automation for business processes — combine AI, APIs, and workflow design to reduce repetitive work without brittle RPA-only approaches.",
    keywords: [
      "intelligent automation company",
      "AI process automation services",
      "business process automation software",
      "workflow automation development",
      "intelligent document processing",
    ],
    outcomes: [
      "Fewer manual handoffs",
      "Faster cycle times on high-volume processes",
      "Automation that adapts with AI where rules fall short",
    ],
    relatedServices: ["/services/artificial-intelligence", "/services/system-integration", "/services/it-consulting"],
    faqs: [
      {
        question: "Is this the same as traditional RPA?",
        answer:
          "Not exactly. We use APIs and event-driven workflows first, and add AI for unstructured inputs — more durable than UI-only bots alone.",
      },
    ],
  },
  "predictive-intelligence": {
    seoTitle: "Predictive Analytics & ML Development Company",
    seoDescription:
      "Predictive intelligence systems that convert business data into forecasts, risk signals, and operational decisions — ML engineering for real production use.",
    keywords: [
      "predictive analytics development company",
      "machine learning development services",
      "forecasting model development",
      "predictive intelligence platform",
      "MLOps consulting services",
    ],
    outcomes: [
      "Earlier signals for demand, risk, and quality",
      "Models connected to operator workflows",
      "Monitoring for drift and reliability",
    ],
    relatedServices: ["/services/artificial-intelligence", "/services/data-engineering", "/services/cloud-devops"],
    faqs: [
      {
        question: "Do you only build models or also the product around them?",
        answer:
          "Both. Predictions only create value when embedded in apps, alerts, and decisions — we ship the full path from data to user experience.",
      },
    ],
  },
  "ai-powered-cx": {
    seoTitle: "AI-Powered Customer Experience Development",
    seoDescription:
      "AI-powered CX platforms — intelligent support, personalization, and customer assistants that feel precise, brand-safe, and measurable.",
    keywords: [
      "AI customer experience development",
      "AI customer support software",
      "conversational AI development company",
      "customer service AI chatbot development",
      "personalized CX platform development",
    ],
    outcomes: [
      "Faster first response and resolution",
      "Consistent brand voice with grounded answers",
      "Insights from conversations into product and ops",
    ],
    relatedServices: [
      "/services/artificial-intelligence",
      "/services/web-app-development",
      "/services/mobile-app-development",
    ],
    faqs: [
      {
        question: "How do you keep AI CX on-brand and accurate?",
        answer:
          "We ground responses in approved knowledge, set tone guidelines, add escalation to humans, and measure containment, CSAT, and hallucination rates.",
      },
    ],
  },
};

export const industryLandings: IndustryLanding[] = industryBase.map((item) => {
  const extra = industryExtras[item.slug];
  if (!extra) {
    throw new Error(`Missing SEO extras for industry ${item.slug}`);
  }
  return {
    name: item.name,
    slug: item.slug,
    description: item.description,
    ...extra,
  };
});

export const solutionLandings: SolutionLanding[] = aiSolutions.map((item) => {
  const slug = slugifySolution(item.title);
  const extra = solutionExtras[slug];
  if (!extra) {
    throw new Error(`Missing SEO extras for solution ${slug}`);
  }
  return {
    title: item.title,
    slug,
    description: item.description,
    ...extra,
  };
});

export function getIndustryBySlug(slug: string) {
  return industryLandings.find((item) => item.slug === slug);
}

export function getSolutionBySlug(slug: string) {
  return solutionLandings.find((item) => item.slug === slug);
}
